import PropTypes from 'prop-types';
import useNetwork from '../common/hooks/networkHook';
import { useJobMessageHistory } from '../reducers/messageHistorySlice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { safeHtml } from '../common/utils/safeHtml';
import { Col, Row } from '../common/components/Grid';
import { cdate } from '../common/utils/CDate';
import useClientConfig from '../common/hooks/clientConfigHook';
import TextareaInput from '../common/components/TextareaInput';
import Button from '../common/components/Button';
import Icon from '../common/components/Icon';
import { useStdRepliesByKind } from '../reducers/stdReplySlice';
import Select from '../common/components/Select';
import { emailJobReply, smsJobReply, voiceJobReply } from '../utils/messagingUtilities';
import { useApp } from '../common/components/AppProvider';
import { useSpecializedTranslation } from '../hooks/specializedTranslationHook';
import { useJob } from '../reducers/jobSlice';


const MESSAGE_LENGTH = 160;

/**
 * Render job notes in a list with a text box to add new notes and option to strike an incorrect entry
 * @component
 */
const JobConversation = ({ jobid, disabled = false }) => {
    const App = useApp();
    const network = useNetwork();
    const job = useJob(jobid);
    const { t } = useSpecializedTranslation(job);
    const messages = useJobMessageHistory(jobid, job?.channel);
    const stdReplies = useStdRepliesByKind(job?.channel);
    const clientConfig = useClientConfig();
    const scrollableContainerRef = useRef();
    const inputRef = useRef();
    const [newMessage, setNewMessage] = useState();

    useEffect(() => {
        setNewMessage();
    }, [jobid])

    const remainder = useMemo(() => MESSAGE_LENGTH - (newMessage?.length ?? 0), [newMessage?.length])

    const processMessage = text => {
        const pattern = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g
        let matches = text.match(pattern);
        // direct href links don't work when running a webframe (it replace sthe app) so skip this step when running in the app
        if (!App.isApp) {
            matches?.forEach(url => text = text.replace(url, '<a href="' + url + '" target="_blank">' + url + '</a>'));
        }
        return text.replaceAll('\n','<br>\n');
    }

    let getMessageContent = (message) => {
        return <>
        <span>{safeHtml(processMessage(message.message))}</span>
        <Row>
            <Col md={6} style={{textAlign: "left"}}>{message.inbound ? message.receivers[0] : null}</Col>
            <Col md={6} style={{textAlign: "right"}}>{cdate(message.timestamp).dateTimeFormat(clientConfig.tz)}</Col>
        </Row>
        </>
    }

    const sendMessage = newMessage => {
        switch(job?.channel) {
            case "SMS":
                smsJobReply(network, jobid, newMessage);
                break;
            case "Voice":
                voiceJobReply(network, jobid, newMessage);
                break;
            case "Email":
                emailJobReply(network, jobid, t("emailJobReplySubject", { jobid: job?.displayid }), newMessage);
                break;
            default:
                throw new Error("Unknown job origin type " + job?.channel);
        }
    }

    const keypress = (e) => {
        if (!disabled && newMessage && newMessage.trim !== '' && !e.shiftKey && e.keyCode === 13) {
            e.stopPropagation();
            e.preventDefault();
            sendMessage(newMessage);
            setNewMessage();
        }
    }

    const postMessage = () => {
        if (!disabled && newMessage && newMessage.trim !== '') {
            sendMessage(newMessage);
            setNewMessage();
        }
    }

    const addStdReply = reply => {
        setNewMessage(newMessage ? `${newMessage} ${reply}` : reply);
        inputRef.current.focus();
    }

    return <div className="d-flex flex-column h-100">
        <div ref={scrollableContainerRef} className="pb-2" style={{ flex: 1, overflowX: "none", overflowY: "auto", overflowAnchor: "none"}}>
            {messages.filter(m => m.type === job.channel).map(msg => <div key={msg.id} className={msg.inbound ? 'speech-bubble-left' : 'speech-bubble-right'}>{getMessageContent(msg)}</div>)}
        </div>
        {disabled ? null : <>
            <Select placeholder={t("stdReplies")} hideLabel={true} onChange={addStdReply} options={stdReplies} labelField="reply" valueField="reply"></Select>
            <TextareaInput ref={inputRef} rows={3} live={true} value={newMessage} onChange={setNewMessage} placeholder={t('enterMessage')} hideLabel={true} onKeyDown={keypress} appendCtrl={<Button variant="success" disabled={!newMessage?.length > 0} onClick={postMessage}><Icon variant="regular" kind="paper-plane"></Icon></Button>}></TextareaInput><span className="mx-2">{t('charactersRemain', { remainder, count: remainder })}</span></>
        }
    </div>
}

JobConversation.propTypes = {
    /**
     * The jobid whose conversation is to be displayed
     */
    jobid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool
}

export default JobConversation;